import styled from 'styled-components'
import { motion } from 'framer-motion'
import TeamMemberCard from '../components/TeamMemberCard'
import CTASection from '../components/CTASection'

const PageHeader = styled.section`
  padding: calc(var(--header-height) + 64px) 24px 64px;
  text-align: center;
  position: relative;
`

const HeaderGlow = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at 50% 0%, var(--color-accent-glow) 0%, transparent 60%);
  pointer-events: none;
`

const HeaderContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 700px;
  margin: 0 auto;
`

const Eyebrow = styled.p`
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--color-accent);
  margin-bottom: 16px;
`

const PageTitle = styled.h1`
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  margin-bottom: 20px;
  line-height: 1.15;
`

const PageSubtitle = styled.p`
  font-size: 1.15rem;
  color: var(--color-text-secondary);
  line-height: 1.7;
`

const FounderSection = styled.section`
  padding: 64px 24px 96px;
`

const FounderInner = styled.div`
  max-width: var(--max-width);
  margin: 0 auto;
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 48px;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    max-width: 600px;
  }
`

const FounderPhoto = styled(motion.div)`
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid var(--color-border);

  img {
    width: 100%;
    aspect-ratio: 1;
    object-fit: cover;
  }
`

const FounderBio = styled(motion.div)`
  h2 {
    font-size: 1.6rem;
    font-weight: 700;
    margin-bottom: 4px;
  }

  h3 {
    font-size: 1rem;
    font-weight: 500;
    color: var(--color-accent);
    margin-bottom: 20px;
  }

  p {
    font-size: 0.95rem;
    color: var(--color-text-secondary);
    line-height: 1.8;
    margin-bottom: 16px;
  }
`

const NetworkSection = styled.section`
  padding: 96px 24px;
  background: var(--color-bg-elevated);
`

const NetworkInner = styled.div`
  max-width: var(--max-width);
  margin: 0 auto;
`

const SectionLabel = styled.p`
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--color-accent);
  margin-bottom: 12px;
  text-align: center;
`

const SectionTitle = styled.h2`
  font-size: clamp(1.8rem, 3vw, 2.5rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  margin-bottom: 16px;
  text-align: center;
`

const SectionSubtitle = styled.p`
  font-size: 1.05rem;
  color: var(--color-text-secondary);
  text-align: center;
  max-width: 640px;
  margin: 0 auto 56px;
  line-height: 1.7;
`

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    max-width: 400px;
    margin: 0 auto;
  }
`

const StorySection = styled.section`
  padding: 96px 24px;
`

const StoryInner = styled.div`
  max-width: 720px;
  margin: 0 auto;
`

const StoryTitle = styled(motion.h2)`
  font-size: clamp(1.8rem, 3vw, 2.5rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  margin-bottom: 32px;
  text-align: center;
`

const StoryText = styled(motion.div)`
  p {
    font-size: 1.05rem;
    color: var(--color-text-secondary);
    line-height: 1.8;
    margin-bottom: 20px;
  }

  strong {
    color: var(--color-text);
  }
`

const NETWORK = [
  {
    name: 'Bertrand',
    role: 'Operations',
    bio: 'Management consultant with 10+ years in Digital Transformation Strategy and Business Operations. Trained software engineer who leverages technology to help companies innovate and deliver exceptional experiences.',
    photo: '/images/headshots/bertrand.jpeg',
    linkedin: 'https://www.linkedin.com/in/bertrandchevalier/',
  },
  {
    name: 'John',
    role: 'Business Dev & Marketing',
    bio: 'Business development and marketing leader with 10+ years in strategic partnerships, growth marketing, and AI/blockchain technologies. Founder of aiverything.ai, automating processes with AI agents.',
    photo: '/images/headshots/john.jpeg',
    linkedin: 'https://www.linkedin.com/in/johnlipe/',
  },
  {
    name: 'Peter',
    role: 'Software Engineering',
    bio: 'Software engineer passionate about performant systems. Led development in distributed systems, IoT, data pipelines, and web dev. Deep expertise in Rust and backend systems with enterprise clients.',
    photo: '/images/headshots/peter.jpg',
    linkedin: 'https://www.linkedin.com/in/petermulard/',
  },
  {
    name: 'Nadia',
    role: 'Marketing, PR & Compliance',
    bio: 'Experienced editor, content strategist, and project manager with 20+ years across Advertising, Media, Entertainment and Publishing. Expertise in EU, US, and Chinese laws on SaaS, emerging tech, and privacy.',
    photo: '/images/headshots/nadia.jpg',
    linkedin: 'https://www.linkedin.com/in/nadiaho/',
  },
  {
    name: 'Matthew',
    role: 'UI/UX Design',
    bio: 'Product Designer with 15+ years of experience. Led UI/UX for Citibank, P&G, Bank of America. Award-winning work including 2024 Fintech Futures Award and 2023 Celent Award.',
    photo: '/images/headshots/matt.jpg',
    linkedin: 'https://www.linkedin.com/in/redheadpro/',
  },
  {
    name: 'Curious Creative',
    role: 'Branding, Content & Digital',
    bio: 'Boutique design firm with 10+ years working with clients on branding, content creation, and design. From startups to global corporations, they take brands from Point A to Z.',
    photo: '/images/logos/curious-creative.png',
    linkedin: 'https://curiouscreativeagency.com/',
  },
]

export default function About() {
  return (
    <>
      <PageHeader>
        <HeaderGlow />
        <HeaderContent>
          <Eyebrow>About</Eyebrow>
          <PageTitle>A network of experts, not an agency</PageTitle>
          <PageSubtitle>
            We're not an agency with overhead. We're a network of specialists who come together
            around your specific needs, backed by a production AI platform.
          </PageSubtitle>
        </HeaderContent>
      </PageHeader>

      <FounderSection>
        <FounderInner>
          <FounderPhoto
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <img src="/images/headshots/coy.jpg" alt="Coy Byron" />
          </FounderPhoto>
          <FounderBio
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <h2>Coy Byron</h2>
            <h3>Founder — Growth, Strategy & AI</h3>
            <p>
              Over the course of his 20+ year career as a global management consultant, sales and business
              development leader, and operator in AI and Web3/Blockchain, Coy has helped Fortune 500,
              Global 1000 brands, and startups generate over $1 billion in business value.
            </p>
            <p>
              Companies he's worked with include Allstate, JP Morgan Chase, The Coca-Cola Company,
              MasterCard, SAP, Sprint, Pfizer, Merck, Sempra Energy, Nissan, DHS, TD Bank,
              Guardian Life Insurance, Reinsurance Group of America, NTT Data, and Daiichi Life.
            </p>
            <p>
              He built DoMoreWorld — a production AI platform with a Council of Agents, voice AI,
              community infrastructure, and commerce tools. That platform is now the foundation
              of Valyss for Business.
            </p>
          </FounderBio>
        </FounderInner>
      </FounderSection>

      <NetworkSection>
        <NetworkInner>
          <SectionLabel>Expert Partner Network</SectionLabel>
          <SectionTitle>Trusted specialists, on demand</SectionTitle>
          <SectionSubtitle>
            Depending on your needs, we bring in trusted subject matter experts in technology,
            operations, marketing, branding, PR, and design.
          </SectionSubtitle>
          <TeamGrid>
            {NETWORK.map((member, i) => (
              <TeamMemberCard key={i} {...member} delay={i * 0.08} />
            ))}
          </TeamGrid>
        </NetworkInner>
      </NetworkSection>

      <StorySection>
        <StoryInner>
          <StoryTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            The Valyss for Business story
          </StoryTitle>
          <StoryText
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <p>
              Valyss for Business started with a belief: <strong>people don't need more apps; they need
              one guide that knows them.</strong> That belief became DoMoreWorld — a platform
              with AI agents that help people discover their purpose, build their plans,
              and connect with community.
            </p>
            <p>
              Along the way, we built something powerful: a production-grade AI platform
              with provider-agnostic chat, function-calling tools, voice capabilities,
              social content automation, and community infrastructure. All running on
              Firebase, serving real users.
            </p>
            <p>
              <strong>Valyss for Business</strong> makes that technology available to companies.
              Small and medium businesses that can't hire developers but need AI to stay competitive.
              We don't just hand them software; we show up, understand their business,
              configure the platform for their needs, and stay with them as they grow.
            </p>
            <p style={{ marginTop: '24px' }}>
              <a 
                href="https://app.domoreworld.com" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ 
                  color: 'var(--color-accent)', 
                  fontWeight: '600',
                  textDecoration: 'none',
                  borderBottom: '2px solid var(--color-accent)'
                }}
              >
                See DoMoreWorld live →
              </a>
              {' '}to experience the platform in action.
            </p>
          </StoryText>
        </StoryInner>
      </StorySection>

      <CTASection
        title="Let's work together"
        subtitle="Book a free discovery call and let's talk about what your business needs."
      />
    </>
  )
}
